/**
 * Weekly Panels Store
 *
 * Zustand store for managing Pocket Sticky Notes state:
 * - Goals of the Week panel
 * - Focus of the Week panel
 * - Panel positions and collapsed states
 */

import { create } from 'zustand';
import type {
  PanelType,
  Position,
  PanelState,
  PanelSize,
} from '../types/weeklyPanels';
import {
  defaultPanelStates,
} from '../types/weeklyPanels';

interface WeeklyPanelsState {
  // Content
  goalsContent: string;
  focusContent: string;

  // Panel states
  goalsPanel: PanelState;
  focusPanel: PanelState;

  // Loading state
  isLoading: boolean;
  isInitialized: boolean;

  // Actions - Content
  setGoalsContent: (content: string) => void;
  setFocusContent: (content: string) => void;
  setContent: (panelType: PanelType, content: string) => void;

  // Actions - Panel state
  toggleCollapsed: (panelType: PanelType) => void;
  setCollapsed: (panelType: PanelType, collapsed: boolean) => void;
  setPosition: (panelType: PanelType, position: Position) => void;
  setSize: (panelType: PanelType, size: PanelSize) => void;

  // Actions - Bulk state
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  hydrate: (data: {
    goalsContent?: string;
    focusContent?: string;
    goalsPanel?: PanelState;
    focusPanel?: PanelState;
  }) => void;
  reset: () => void;
}

/**
 * Initial state
 */
const initialState = {
  goalsContent: '',
  focusContent: '',
  goalsPanel: defaultPanelStates.goals,
  focusPanel: defaultPanelStates.focus,
  isLoading: false,
  isInitialized: false,
};

/**
 * Weekly Panels Zustand Store
 */
export const useWeeklyPanelsStore = create<WeeklyPanelsState>((set) => ({
  ...initialState,

  // Content Actions
  setGoalsContent: (content) => set({ goalsContent: content }),
  setFocusContent: (content) => set({ focusContent: content }),
  setContent: (panelType, content) => {
    if (panelType === 'goals') {
      set({ goalsContent: content });
    } else {
      set({ focusContent: content });
    }
  },

  // Panel State Actions
  toggleCollapsed: (panelType) =>
    set((state) => {
      if (panelType === 'goals') {
        return {
          goalsPanel: {
            ...state.goalsPanel,
            isCollapsed: !state.goalsPanel.isCollapsed,
          },
        };
      }
      return {
        focusPanel: {
          ...state.focusPanel,
          isCollapsed: !state.focusPanel.isCollapsed,
        },
      };
    }),

  setCollapsed: (panelType, collapsed) =>
    set((state) => {
      if (panelType === 'goals') {
        return {
          goalsPanel: {
            ...state.goalsPanel,
            isCollapsed: collapsed,
          },
        };
      }
      return {
        focusPanel: {
          ...state.focusPanel,
          isCollapsed: collapsed,
        },
      };
    }),

  setPosition: (panelType, position) =>
    set((state) => {
      if (panelType === 'goals') {
        return {
          goalsPanel: {
            ...state.goalsPanel,
            position,
          },
        };
      }
      return {
        focusPanel: {
          ...state.focusPanel,
          position,
        },
      };
    }),

  setSize: (panelType, size) =>
    set((state) => {
      if (panelType === 'goals') {
        return {
          goalsPanel: {
            ...state.goalsPanel,
            size,
          },
        };
      }
      return {
        focusPanel: {
          ...state.focusPanel,
          size,
        },
      };
    }),

  // Bulk State Actions
  setLoading: (loading) => set({ isLoading: loading }),
  setInitialized: (initialized) => set({ isInitialized: initialized }),

  hydrate: (data) =>
    set((state) => ({
      goalsContent: data.goalsContent ?? state.goalsContent,
      focusContent: data.focusContent ?? state.focusContent,
      goalsPanel: data.goalsPanel ?? state.goalsPanel,
      focusPanel: data.focusPanel ?? state.focusPanel,
      isInitialized: true,
    })),

  reset: () => set(initialState),
}));
